import { z } from 'zod';
import { Id, Timestamp } from './common';
import { endpoint } from './endpoint';
import { FileInfo } from './files';
import { Person } from './users';

/**
 * Permission-aware search (R30): only conversations the viewer belongs to and
 * only within their visible history range; deleted content is excluded.
 * Queries of ≤ 2 characters are limited to the last 30 days and 50 results.
 */
export const MessageHit = z.object({
  conversationId: Id,
  conversationTitle: z.string(),
  messageId: Id,
  senderId: Id.nullable(),
  text: z.string(),
  createdAt: Timestamp,
});
export type MessageHit = z.infer<typeof MessageHit>;

export const FileHit = z.object({ conversationId: Id, conversationTitle: z.string(), file: FileInfo });
export type FileHit = z.infer<typeof FileHit>;

export const searchEndpoints = {
  search: endpoint({
    id: 'search.query',
    method: 'GET',
    path: '/api/v1/search',
    auth: 'session',
    query: z.object({
      q: z.string().trim().min(1).max(100),
      /** Restrict to one conversation ("대화 내 검색"); people results are then empty. */
      conversationId: Id.optional(),
      limit: z.coerce.number().int().min(1).max(100).default(30),
    }),
    /** One request fills every tab; each list holds at most `limit` items (newest first). */
    response: z.object({
      messages: z.array(MessageHit),
      files: z.array(FileHit),
      people: z.array(Person),
      /** Total matches per tab (may exceed the returned lists). */
      counts: z.object({ messages: z.number().int(), files: z.number().int(), people: z.number().int() }),
      limited: z.object({ shortQuery: z.boolean(), sinceDays: z.number().int().nullable(), maxResults: z.number().int().nullable() }),
    }),
    rateLimit: 'search',
  }),
};
